import { Cpu } from "lucide-react";
import { SectionCard } from "@/components/SectionCard";
import type { InstanceStepModelView, ProcessInstanceView } from "@/types";
import { anforderungsChips, gesperrt, KEINE_WAHL, optionen, vorlageSatz, wirkungsSatz } from "./stepModelSatz";

// #P002 (UC-3): „Modell je Schritt" an der Einrichtung.
//
// Die Vorlage sagt, welche ART ein Schritt braucht (und nennt optional einen Namen); die
// Einrichtung wählt DARIN. Genau das zeigt die Karte: je Agent-Schritt eine Zeile mit den
// Anforderungen, dem Fakt der Vorlage, einem Select aus den ERFÜLLENDEN Modellen — und einem
// Satz, der sagt, womit der Schritt danach läuft.
//
// Drei Dinge, die man beim Erweitern nicht brechen darf:
//  · Ein Schritt mit harter VORGABE bekommt kein Bedien-Element — weder gesperrt noch
//    sichtbar. Dort gibt es keine Wahl dieser Ebene (der Master lehnt sie mit 400 ab).
//  · Gewählt wird aus `erfuellend` (Server-Wahrheit), nie getippt: ein getippter Name wäre
//    nicht überprüfbar und liefe in ein generisches 400.
//  · `erfuellend === null` heisst „nicht ermittelbar", nicht „nichts" — dann steht die Zeile
//    da, sagt das, und eine gespeicherte Wahl bleibt sicht- und entfernbar.

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-base px-2.5 py-1 text-[13px] text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";

export function StepModelCard({
  instance,
  /** Nur die in DIESER Sitzung berührten Schritte — der Rest bleibt unangetastet. */
  beruehrt,
  onChange,
  darfEdit,
}: {
  instance: ProcessInstanceView;
  beruehrt: Record<string, string>;
  onChange: (stepId: string, wert: string) => void;
  darfEdit: boolean;
}) {
  // ⚠ DREI Zustände, drei Bedeutungen (Muster `gates`, #330):
  //  · `undefined` = älterer Master ⇒ die Karte erscheint GAR NICHT.
  //  · `null` = Definition nicht lesbar ⇒ die Karte erscheint und sagt genau das.
  //  · `[]` = wirklich kein Agent-Schritt ⇒ nichts zu zeigen.
  if (instance.stepModels === undefined) return null;
  const unlesbar = instance.stepModels === null;
  const zeilen = instance.stepModels ?? [];
  if (!unlesbar && zeilen.length === 0) return null;

  return (
    <SectionCard
      icon={<Cpu className="h-[18px] w-[18px]" />}
      title="Modell je Schritt"
      description="Welches Modell einen Schritt fährt — aus den Modellen, die seine Anforderungen erfüllen"
    >
      {unlesbar ? (
        <p className="text-[12.5px] text-text3" data-testid="instance-stepmodel-unlesbar">
          Die Prozess-Definition ist gerade nicht abrufbar — die Schritte lassen sich deshalb
          nicht anzeigen. Bestehende Einstellungen bleiben unberührt.
        </p>
      ) : (
        <div className="space-y-3.5">
          {zeilen.map((zeile) => (
            <Zeile
              key={zeile.stepId}
              zeile={zeile}
              beruehrt={beruehrt[zeile.stepId]}
              onChange={(wert) => onChange(zeile.stepId, wert)}
              darfEdit={darfEdit}
            />
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function Zeile({
  zeile,
  beruehrt,
  onChange,
  darfEdit,
}: {
  zeile: InstanceStepModelView;
  beruehrt: string | undefined;
  onChange: (wert: string) => void;
  darfEdit: boolean;
}) {
  const chips = anforderungsChips(zeile);
  const fix = gesperrt(zeile);
  const wert = beruehrt !== undefined ? beruehrt : (zeile.chosen ?? KEINE_WAHL);
  const opts = optionen(zeile, beruehrt);

  return (
    <div className="rounded-md border border-border bg-base p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-text">{zeile.stepName}</span>
        {chips.map((c) => (
          <span
            key={c}
            className="rounded-full border border-line px-1.5 py-0.5 text-[11px] text-text2"
            data-testid={`instance-stepmodel-${zeile.stepId}-chip-${c}`}
          >
            {c}
          </span>
        ))}
        <span className="text-[11.5px] text-text3">{vorlageSatz(zeile)}</span>
      </div>

      {/* Eine harte Vorgabe bekommt KEIN Bedien-Element — sie ist keine Wahl dieser Ebene. */}
      {!fix && (
        <div className="mt-2 max-w-md">
          <select
            className={SELECT_CLASS}
            value={wert}
            onChange={(e) => onChange(e.target.value)}
            disabled={!darfEdit}
            data-testid={`instance-stepmodel-${zeile.stepId}`}
          >
            <option value={KEINE_WAHL}>
              {zeile.templateModel !== null ? "Vorschlag der Vorlage" : "Lauf-Modell"}
            </option>
            {opts.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {zeile.erfuellend === null && (
            <p className="mt-1 text-[11px] text-text3">
              Die Modelle dieses Agenten sind gerade nicht abrufbar — es lässt sich nichts Neues wählen.
            </p>
          )}
          {zeile.erfuellend !== null && zeile.erfuellend.length === 0 && (
            <p className="mt-1 text-[11px] text-warn">
              Kein Modell dieses Agenten erfüllt die Anforderungen — das löst ein Admin (Slot anlegen
              oder Metadaten setzen).
            </p>
          )}
        </div>
      )}

      <p className="mt-1.5 text-[11.5px] text-text2" data-testid={`instance-stepmodel-${zeile.stepId}-satz`}>
        {wirkungsSatz(zeile, beruehrt)}
      </p>
    </div>
  );
}
